const asDate = (value) => {
  if (!value) return null;
  const raw = typeof value?.toDate === 'function' ? value.toDate() : value;
  const date = raw instanceof Date ? new Date(raw.getTime()) : new Date(raw);
  return Number.isNaN(date.getTime()) ? null : date;
};

export const ALL_FINANCIAL_YEARS = '__ALL_FINANCIAL_YEARS__';

export const getSelectedFinancialYear = () => {
  if (typeof window === 'undefined') return null;
  return window.__COLD_STORAGE_FINANCIAL_YEAR__ || null;
};

export const getFinancialYearBounds = (financialYear = getSelectedFinancialYear()) => {
  if (!financialYear) return { startDate: null, endDate: null };
  const startDate = asDate(financialYear.startDate);
  const endDate = asDate(financialYear.endDate);
  if (startDate) startDate.setHours(0, 0, 0, 0);
  if (endDate) endDate.setHours(23, 59, 59, 999);
  return { startDate, endDate };
};

export const recordFinancialDate = (row = {}) => asDate(
  row.financialDate ||
  row.transactionDate || row.entryDate || row.postingDate ||
  row.inwardDate || row.gateDate || row.weighmentDate || row.unloadingDate ||
  row.outwardDate || row.dispatchDate || row.deliveryDate || row.loadingDate ||
  row.requestDate || row.agreementDate || row.invoiceDate || row.receiptDate ||
  row.paymentDate || row.expenseDate || row.incomeDate || row.storageDate ||
  row.storageAllocationDate || row.lotCreationDate || row.gradingDate ||
  row.tradeDate || row.purchaseDate || row.saleDate || row.transferDate ||
  row.issueDate || row.returnDate || row.adjustmentDate || row.attendanceDate ||
  row.leaveDate || row.payrollDate || row.billingDate || row.recordedAt ||
  row.date || row.createdAt
);

export const financialYearSnapshot = (financialYear = getSelectedFinancialYear()) => ({
  financialYearId: financialYear?.id || '',
  financialYearName: financialYear?.name || financialYear?.year || financialYear?.label || '',
  financialYearStartDate: financialYear?.startDate || null,
  financialYearEndDate: financialYear?.endDate || null
});

export const withFinancialYear = (data = {}, financialYear = getSelectedFinancialYear()) => {
  if (!financialYear?.id) return { ...data };
  const fy = financialYearSnapshot(financialYear);
  return {
    ...data,
    financialYearId: data.financialYearId || fy.financialYearId,
    financialYearName: data.financialYearName || fy.financialYearName,
    financialYearStartDate: data.financialYearStartDate || fy.financialYearStartDate,
    financialYearEndDate: data.financialYearEndDate || fy.financialYearEndDate
  };
};

export const withSelectedFinancialYear = (data = {}) => withFinancialYear(data, getSelectedFinancialYear());

export const matchesFinancialYear = (row = {}, financialYear = getSelectedFinancialYear()) => {
  if (!financialYear?.id) return true;

  const explicit = row.financialYearId || row.financialYear?.id || row.financialYearRef?.id || '';
  if (explicit) return String(explicit) === String(financialYear.id);

  const date = recordFinancialDate(row);
  const { startDate, endDate } = getFinancialYearBounds(financialYear);
  // Legacy operational rows without explicit FY are date-scoped when possible.
  // Rows with neither a business date nor a created date are kept visible so
  // old master/config records are not accidentally lost.
  if (!date || !startDate || !endDate) return true;
  return date >= startDate && date <= endDate;
};

export const matchesSelectedFinancialYear = (row = {}) =>
  matchesFinancialYear(row, getSelectedFinancialYear());

export const filterFinancialYear = (rows = [], financialYear = getSelectedFinancialYear()) =>
  Array.isArray(rows) ? rows.filter((row) => matchesFinancialYear(row, financialYear)) : [];

export const filterSelectedFinancialYear = (rows = []) =>
  filterFinancialYear(rows, getSelectedFinancialYear());

export const FINANCIAL_YEAR_SCOPED_COLLECTIONS = new Set([
  'agreements', 'inwards', 'inward', 'gatePasses', 'gateEntries', 'weighments',
  'unloadings', 'inspections', 'gradings', 'unloadingLabour', 'varietyGroups',
  'lots', 'storageAllocations', 'stockMovements', 'packedBalances', 'product_transfers',
  'rejections', 'outwards', 'dispatches', 'loading', 'delivery', 'purchases', 'sales', 'trades', 'trading', 'tradePayments',
  'invoices', 'receipts', 'payments', 'expenses', 'income', 'manualIncome',
  'financeTransactions', 'accountTransactions', 'ledger', 'transactions', 'attendance',
  'leaves', 'payroll', 'crateGatePasses', 'crateMovements', 'auditLogs', 'locationAuditLogs',
  'binAuditLogs', 'growerLedger', 'supplierLedger', 'stockTransactions',
  'stockPurchases', 'stockIssues', 'stockOwnershipTransfers', 'storageReceipts',
  'storageInvoices', 'gradingRuns', 'gradingInvoices', 'outwardInvoices',
  'crateLedger', 'financeExpenses', 'financeIncome', 'financeTransactions'
]);

export const isFinancialYearScopedCollection = (collectionName = '') =>
  FINANCIAL_YEAR_SCOPED_COLLECTIONS.has(String(collectionName || '').trim());

export const filterCollectionBySelectedFinancialYear = (collectionName, rows = []) =>
  isFinancialYearScopedCollection(collectionName) ? filterSelectedFinancialYear(rows) : rows;

export const financialYearLabel = (financialYear) =>
  financialYear?.name || financialYear?.year || financialYear?.label || 'All Financial Years';

export const shiftDateBetweenFinancialYears = (value, sourceYear, targetYear) => {
  const original = asDate(value);
  const sourceStart = asDate(sourceYear?.startDate);
  const targetStart = asDate(targetYear?.startDate);
  if (!original || !sourceStart || !targetStart) return value;
  const offsetMs = original.getTime() - sourceStart.getTime();
  return new Date(targetStart.getTime() + offsetMs);
};
